import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  HostBinding,
  HostListener,
  inject,
  Injector,
  input,
  isSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, Event as RouterEvent } from '@angular/router';
import { Dictionary, Store } from '@softline/core';
import { filter, map, of, switchMap } from 'rxjs';
import { VirtualKeyboardService } from '../../../keyboard/virtual-keyboard.service';
import { BreakpointService } from '../../../utilities/breakpoints/services/breakpoint.service';
import { Modal2, SOFTLINE_MODAL_RESOLVER } from '../../abstraction/modal';
import { SOFTLINE_FEATURE_MODAL } from '../../legacy/modal.shared';
import * as ModalStore from '../../legacy/modal.store';
import { ModalService } from '../../modal.service';
import { InternalModalConfig } from '../../types/modal-config';

@Component({
  selector: 'soft-modal',
  imports: [CommonModule],
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.css'],
  host: {
    '[style.bottom]': 'modalBottomOffset()',
  },
})
export class ModalComponent {
  protected readonly virtualKeyboardService = inject(VirtualKeyboardService);
  protected readonly breakpointService = inject(BreakpointService);
  protected readonly router = inject(Router);

  config = input.required<InternalModalConfig<Modal2, unknown>>();
  config$ = toObservable(this.config);
  private modalService = inject(ModalService);
  private store = inject(Store);

  active = input<boolean>(false);

  title = computed(() => {
    const config = this.config();
    return config.title ? config.title() : null;
  });

  subtitle = computed(() => {
    const config = this.config();
    return config.subtitle ? config.subtitle() : null;
  });

  readonly displayedAsBottomSheet = computed(() => {
    const breakpoint = this.breakpointService.breakpoint();
    return !breakpoint;
  });

  // change offset on mobile when virtual keyboard is present
  readonly modalBottomOffset = computed(() => {
    const isMobile = this.displayedAsBottomSheet();
    if (!isMobile) return 'auto';
    else if (this.virtualKeyboardService.open()) return '256px';
    else return '0';
  });

  text = computed(() => {
    const config = this.config();
    return config.text ? config.text() : null;
  });

  template = computed(() => {
    const config = this.config();
    return config.template ? config.template() : null;
  });

  templateContext = computed(() => {
    const config = this.config();
    return config.templateContext ? config.templateContext() : null;
  });

  readonly contentClass = computed(() => {
    const userSpecifiedClass = this.config()?.contentClass;
    return !userSpecifiedClass
      ? 'contents'
      : `soft-custom-content-class ${userSpecifiedClass} `;
  });

  injector = computed(() => {
    return Injector.create({
      providers: [
        { provide: SOFTLINE_MODAL_RESOLVER, useValue: (result) => this.resolve(result) },
      ],
      parent: this.config().injector,
    });
  });

  componentInputs = computed(() => {
    const data = this.config().data;
    const componentInputs: Dictionary<unknown> = {};
    for (const [key, value] of Object.entries(data ?? {})) {
      if (isSignal(value)) componentInputs[key] = value();
      else componentInputs[key] = value;
    }
    return componentInputs;
  });

  constructor() {
    const previousUrl = this.router.url;
    this.config$
      .pipe(
        switchMap((config) => {
          if (!config) return of(false);
          if (config.dismissOnNavigation !== false) {
            return this.router.events.pipe(
              filter(
                (event: RouterEvent): event is NavigationEnd =>
                  event instanceof NavigationEnd
              ),
              map((event: NavigationEnd) => {
                const currentUrl = event.urlAfterRedirects;
                const normalizedCurrent = this.normalizeUrl(currentUrl);
                const normalizedPrevious = this.normalizeUrl(previousUrl);
                return normalizedCurrent !== normalizedPrevious;
              })
            );
          }
          return of(false);
        }),
        takeUntilDestroyed()
      )
      .subscribe((shouldDismiss) => {
        if (shouldDismiss) this.dismissAll();
      });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event): void {
    this.dismiss(event);
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  @HostBinding('class')
  get class() {
    return this.config().class;
  }

  resolve(result: unknown): void {
    const config = this.config();
    if (config.callback) config.callback(result);
    this.modalService.close(config.id);
  }

  dismiss(event?: Event): void {
    if (this.store.get(SOFTLINE_FEATURE_MODAL, ModalStore.getters.count) > 0) return;

    const config = this.config();
    if (config.dismiss === false || !this.active()) return;

    if (config.onDismiss) config.onDismiss();

    this.modalService.close(config.id);

    if (event) {
      event.stopPropagation();
    }
  }

  dismissAll(): void {
    const configs = this.modalService.configs();
    configs.forEach((config) => {
      if (config.dismiss !== false) {
        this.modalService.close(config.id);
      }
    });
  }

  normalizeUrl = (url: string): string => {
    return url.split('#')[0].split('?')[0].split(';')[0];
  };
}
